import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShoppingBagIcon, ChevronLeftIcon, PlusIcon, TagIcon, PackageIcon, SearchIcon, FilterIcon, LayoutGridIcon, ListIcon, QrCodeIcon, LinkIcon, TrashIcon, EditIcon, EyeIcon, DollarSignIcon, ArrowLeftIcon, StoreIcon, CheckCircleIcon, XCircleIcon, ImageIcon } from 'lucide-react';
import { Shop } from './Shops';
import { ShopDashboard } from '../components/ShopDashboard';
// Product type definition
interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  discountedPrice?: number;
  categoryId: string;
  imageUrl: string;
  stockQuantity: number;
  sku: string;
  isActive: boolean;
}
// Category type definition
interface Category {
  id: string;
  name: string;
  description: string;
  productCount: number;
}
export function ShopDetails() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shopId = searchParams.get('id');
  const isNewShop = searchParams.get('new') === 'true';
  const isEditMode = searchParams.get('edit') === 'true' || isNewShop;
  // Shop state
  const [shop, setShop] = useState<Shop | null>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'products' | 'categories'>('dashboard');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | 'all'>('all');
  // Products and categories state
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  // Form state
  const [shopForm, setShopForm] = useState({
    name: '',
    description: '',
    logo: '•',
    color: '#f8d7d7',
    channels: ['ecommerce'] as ('pos' | 'ecommerce')[]
  });
  // Product form state
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: 0,
    discountedPrice: undefined as number | undefined,
    categoryId: '',
    imageUrl: '',
    stockQuantity: 0,
    sku: '',
    isActive: true
  });
  // Mock data loading
  useEffect(() => {
    if (shopId && !isNewShop) {
      // Mock shop data
      const mockShop: Shop = {
        id: shopId,
        name: 'Beach Shop',
        description: 'Beachwear and accessories for summer holidays',
        logo: '•',
        color: '#f8d7d7',
        isOpen: true,
        channels: ['pos', 'ecommerce'],
        productsCount: 48,
        revenue: {
          total: 12580,
          currency: 'THB',
          growth: 15.3
        },
        ordersCount: 124
      };
      setShop(mockShop);
      setShopForm({
        name: mockShop.name,
        description: mockShop.description,
        logo: mockShop.logo,
        color: mockShop.color,
        channels: [...mockShop.channels]
      });
      // Mock categories
      setCategories([{
        id: 'cat1',
        name: 'Swimwear',
        description: 'Swimsuits and beachwear',
        productCount: 12
      }, {
        id: 'cat2',
        name: 'Accessories',
        description: 'Beach accessories',
        productCount: 18
      }, {
        id: 'cat3',
        name: 'Footwear',
        description: 'Sandals and beach shoes',
        productCount: 8
      }, {
        id: 'cat4',
        name: 'Souvenirs',
        description: 'Memorable items from the beach',
        productCount: 10
      }]);
      // Mock products
      setProducts([{
        id: 'prod1',
        name: 'Beach Towel',
        description: 'Large absorbent beach towel',
        price: 29.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1601924582970-9238bcb495d9?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 45,
        sku: 'BT-001',
        isActive: true
      }, {
        id: 'prod2',
        name: 'Sunglasses',
        description: 'UV protection sunglasses',
        price: 49.99,
        discountedPrice: 39.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1577803645773-f96470509666?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 32,
        sku: 'SG-002',
        isActive: true
      }, {
        id: 'prod3',
        name: 'Swim Shorts',
        description: 'Quick-dry swim shorts for men',
        price: 34.99,
        categoryId: 'cat1',
        imageUrl: 'https://images.unsplash.com/photo-1565128939522-7a321311972a?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 28,
        sku: 'SS-003',
        isActive: true
      }, {
        id: 'prod4',
        name: 'Beach Sandals',
        description: 'Comfortable sandals for the beach',
        price: 24.99,
        categoryId: 'cat3',
        imageUrl: 'https://images.unsplash.com/photo-1603487742131-4160ec999306?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 50,
        sku: 'BS-004',
        isActive: true
      }, {
        id: 'prod5',
        name: 'Straw Hat',
        description: 'Wide brim straw hat for sun protection',
        price: 19.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1572307480813-ceb0e59d8325?w=300&q=80',
        stockQuantity: 15,
        sku: 'SH-005',
        isActive: true
      }, {
        id: 'prod6',
        name: 'Beach Bag',
        description: 'Spacious waterproof beach bag',
        price: 39.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1590874103328-eac38a683ce7?w=300&q=80',
        stockQuantity: 20,
        sku: 'BB-006',
        isActive: true
      }, {
        id: 'prod7',
        name: 'Surfboard Wax',
        description: 'High quality organic surfboard wax',
        price: 9.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1531722569936-825d3dd91b15?w=300&q=80',
        stockQuantity: 100,
        sku: 'SW-007',
        isActive: true
      }, {
        id: 'prod8',
        name: 'Snorkeling Set',
        description: 'Professional snorkeling mask and fins',
        price: 59.99,
        discountedPrice: 49.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300&q=80',
        stockQuantity: 12,
        sku: 'SN-008',
        isActive: true
      }]);
    }
  }, [shopId, isNewShop]);
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) || product.description.toLowerCase().includes(searchTerm.toLowerCase()) || product.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategoryId === 'all' || product.categoryId === selectedCategoryId;
    return matchesSearch && matchesCategory;
  });
  const saveShop = (e: React.FormEvent) => {
    e.preventDefault();
    if (isNewShop) {
      navigate('/shops');
    } else {
      navigate(`/shop-details?id=${shopId}`);
    }
  };
  const toggleProductStatus = (productId: string) => {
    setProducts(products.map(product => product.id === productId ? {
      ...product,
      isActive: !product.isActive
    } : product));
  };
  const handleAddProductClick = () => {
    setEditingProduct(null);
    setProductForm({
      name: '',
      description: '',
      price: 0,
      discountedPrice: undefined,
      categoryId: categories.length > 0 ? categories[0].id : '',
      imageUrl: '',
      stockQuantity: 0,
      sku: '',
      isActive: true
    });
    setShowProductForm(true);
  };
  const handleEditProductClick = (product: Product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price,
      discountedPrice: product.discountedPrice,
      categoryId: product.categoryId,
      imageUrl: product.imageUrl,
      stockQuantity: product.stockQuantity,
      sku: product.sku,
      isActive: product.isActive
    });
    setShowProductForm(true);
  };
  const handleDeleteProductClick = (productId: string) => {
    if (confirm('Are you sure you want to delete this product?')) {
      setProducts(products.filter(p => p.id !== productId));
    }
  };
  const handleProductFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingProduct) {
      setProducts(products.map(p => p.id === editingProduct.id ? {
        ...productForm,
        id: p.id
      } as Product : p));
    } else {
      const newProduct: Product = {
        ...productForm,
        id: `prod${Date.now()}`
      } as Product;
      setProducts([...products, newProduct]);
    }
    setShowProductForm(false);
  };
  // Render Form Mode
  if (isEditMode && !showProductForm) {
    return <div className="space-y-8 max-w-7xl mx-auto pb-12" data-id="element-10744">
        <div className="flex items-center" data-id="element-10745">
          <button onClick={() => navigate('/shops')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold" data-id="element-10746">
            <ArrowLeftIcon size={20} className="mr-2" data-id="element-10747" />
            {isNewShop ? 'All shops' : 'Back to shop'}
          </button>
        </div>

        <form onSubmit={saveShop} className="space-y-6" data-id="element-10748">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-10749">
            <div className="p-8 border-b border-gray-100" data-id="element-10750">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-10751">
                {isNewShop ? 'New Shop' : 'Edit Shop'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-10752">
                <div data-id="element-10753">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10754">
                    Shop Name <span className="text-red-500" data-id="element-10755">*</span>
                  </label>
                  <input type="text" required value={shopForm.name} onChange={e => setShopForm({
                  ...shopForm,
                  name: e.target.value
                })} placeholder="Enter shop name" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-10756" />
                </div>
                <div data-id="element-10757">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10758">
                    Shop Logo
                  </label>
                  <input type="text" value={shopForm.logo} onChange={e => setShopForm({
                  ...shopForm,
                  logo: e.target.value
                })} placeholder="Logo character or emoji" maxLength={2} className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-10759" />
                  <p className="mt-1.5 text-xs text-gray-500" data-id="element-10760">
                    Enter a single character or emoji to use as shop logo
                  </p>
                </div>
                <div className="md:col-span-2" data-id="element-10761">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10762">
                    Description
                  </label>
                  <textarea value={shopForm.description} onChange={e => setShopForm({
                  ...shopForm,
                  description: e.target.value
                })} rows={3} placeholder="Describe your shop" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-10763" />
                </div>
              </div>
            </div>

            <div className="p-8" data-id="element-10764">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-10765">
                Appearance & Channels
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-10766">
                <div data-id="element-10767">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10768">
                    Logo Background Color
                  </label>
                  <div className="flex items-center gap-3" data-id="element-10769">
                    <input type="color" value={shopForm.color} onChange={e => setShopForm({
                    ...shopForm,
                    color: e.target.value
                  })} className="w-10 h-10 border-0 p-0 rounded cursor-pointer" data-id="element-10770" />
                    <input type="text" value={shopForm.color} onChange={e => setShopForm({
                    ...shopForm,
                    color: e.target.value
                  })} className="flex-1 px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors uppercase" data-id="element-10771" />
                  </div>
                </div>
                <div data-id="element-10772">
                  <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-10773">
                    Shop Channels
                  </label>
                  <div className="space-y-3" data-id="element-10774">
                    <label className="flex items-center cursor-pointer group" data-id="element-10775">
                      <input type="checkbox" checked={shopForm.channels.includes('ecommerce')} onChange={e => {
                      const newChannels = e.target.checked ? [...shopForm.channels, 'ecommerce'] : shopForm.channels.filter(c => c !== 'ecommerce');
                      setShopForm({
                        ...shopForm,
                        channels: newChannels as ('pos' | 'ecommerce')[]
                      });
                    }} className="h-4 w-4 text-[#F7941D] focus:ring-[#F7941D] border-gray-300 rounded transition-colors" data-id="element-10776" />
                      <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900 transition-colors" data-id="element-10777">
                        E-Commerce (Online Shop)
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer group" data-id="element-10778">
                      <input type="checkbox" checked={shopForm.channels.includes('pos')} onChange={e => {
                      const newChannels = e.target.checked ? [...shopForm.channels, 'pos'] : shopForm.channels.filter(c => c !== 'pos');
                      setShopForm({
                        ...shopForm,
                        channels: newChannels as ('pos' | 'ecommerce')[]
                      });
                    }} className="h-4 w-4 text-[#F7941D] focus:ring-[#F7941D] border-gray-300 rounded transition-colors" data-id="element-10779" />
                      <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900 transition-colors" data-id="element-10780">
                        Point of Sale (Physical Store)
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3" data-id="element-10781">
            <button type="button" onClick={() => navigate(isNewShop ? '/shops' : `/shop-details?id=${shopId}`)} className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-10782">
              Cancel
            </button>
            <button type="submit" className="px-6 py-2.5 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-10783">
              {isNewShop ? 'Create shop' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>;
  }
  // Render Product Form Mode
  if (showProductForm) {
    return <div className="space-y-8 max-w-3xl mx-auto pb-12" data-id="element-10784">
        <div className="flex items-center" data-id="element-10785">
          <button onClick={() => setShowProductForm(false)} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-10786">
            <ArrowLeftIcon size={16} className="mr-2" data-id="element-10787" />
            Back to products
          </button>
        </div>

        <form onSubmit={handleProductFormSubmit} className="space-y-6" data-id="element-10788">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-10789">
            <div className="px-8 py-6 border-b border-gray-100" data-id="element-10790">
              <h1 className="text-xl font-semibold text-gray-900" data-id="element-10791">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h1>
            </div>

            <div className="p-8 space-y-10" data-id="element-10792">
              {/* Product Information */}
              <div className="space-y-6" data-id="element-10793">
                <h2 className="text-lg font-medium text-gray-900" data-id="element-10794">
                  Product Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-10795">
                  <div data-id="element-10796">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-10797">
                      Product Name <span className="text-red-500" data-id="element-10798">*</span>
                    </label>
                    <input type="text" required value={productForm.name} onChange={e => setProductForm({
                    ...productForm,
                    name: e.target.value
                  })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-10799" />
                  </div>
                  <div data-id="element-10800">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-10801">
                      SKU
                    </label>
                    <input type="text" value={productForm.sku} onChange={e => setProductForm({
                    ...productForm,
                    sku: e.target.value
                  })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-10802" />
                  </div>
                  <div className="md:col-span-2" data-id="element-10803">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-10804">
                      Description
                    </label>
                    <textarea value={productForm.description} onChange={e => setProductForm({
                    ...productForm,
                    description: e.target.value
                  })} rows={3} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-10805" />
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="relative py-2" data-id="element-10806">
                <div className="absolute inset-0 flex items-center" data-id="element-10807">
                  <div className="w-full border-t border-gray-200" data-id="element-10808"></div>
                </div>
                <div className="relative flex justify-center" data-id="element-10809">
                  <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-10810">
                    Pricing & Inventory
                  </span>
                </div>
              </div>

              {/* Pricing & Inventory */}
              <div className="space-y-5" data-id="element-10811">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-10812">
                  <div data-id="element-10813">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-10814">
                      Price <span className="text-red-500" data-id="element-10815">*</span>
                    </label>
                    <div className="relative" data-id="element-10816">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-10817">
                        <span className="text-gray-500 sm:text-sm" data-id="element-10818">THB</span>
                      </div>
                      <input type="number" required min="0" step="0.01" value={productForm.price || ''} onChange={e => setProductForm({
                      ...productForm,
                      price: parseFloat(e.target.value) || 0
                    })} className="w-full pl-12 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-10819" />
                    </div>
                  </div>
                  <div data-id="element-10820">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-10821">
                      Discounted Price
                    </label>
                    <div className="relative" data-id="element-10822">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-10823">
                        <span className="text-gray-500 sm:text-sm" data-id="element-10824">THB</span>
                      </div>
                      <input type="number" min="0" step="0.01" value={productForm.discountedPrice || ''} onChange={e => setProductForm({
                      ...productForm,
                      discountedPrice: e.target.value ? parseFloat(e.target.value) : undefined
                    })} className="w-full pl-12 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-10825" />
                    </div>
                  </div>
                  <div data-id="element-10826">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-10827">
                      Stock Quantity
                    </label>
                    <input type="number" min="0" value={productForm.stockQuantity || ''} onChange={e => setProductForm({
                    ...productForm,
                    stockQuantity: parseInt(e.target.value) || 0
                  })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-10828" />
                  </div>
                  <div data-id="element-10829">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-10830">
                      Category
                    </label>
                    <select value={productForm.categoryId} onChange={e => setProductForm({
                    ...productForm,
                    categoryId: e.target.value
                  })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors bg-white" data-id="element-10831">
                      {categories.map(c => <option key={c.id} value={c.id} data-id="element-10832">
                          {c.name}
                        </option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="relative py-2" data-id="element-10833">
                <div className="absolute inset-0 flex items-center" data-id="element-10834">
                  <div className="w-full border-t border-gray-200" data-id="element-10835"></div>
                </div>
                <div className="relative flex justify-center" data-id="element-10836">
                  <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-10837">
                    Product Image
                  </span>
                </div>
              </div>

              {/* Product Image */}
              <div className="space-y-5" data-id="element-10838">
                <div data-id="element-10839">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-10840">
                    Image URL
                  </label>
                  <input type="text" value={productForm.imageUrl} onChange={e => setProductForm({
                  ...productForm,
                  imageUrl: e.target.value
                })} placeholder="https://images.unsplash.com/..." className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-10841" />
                </div>
                <div className="mt-4" data-id="element-10842">
                  {productForm.imageUrl ? <div className="w-48 h-48 rounded-lg border border-gray-200 overflow-hidden bg-gray-50" data-id="element-10843">
                      <img src={productForm.imageUrl} alt="Preview" className="w-full h-full object-cover" data-id="element-10844" />
                    </div> : <div className="w-48 h-48 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center bg-gray-50 text-gray-400" data-id="element-10845">
                      <ImageIcon size={32} className="mb-2" data-id="element-10846" />
                      <span className="text-sm" data-id="element-10847">No image</span>
                    </div>}
                </div>
              </div>

              {/* Divider */}
              <div className="relative py-2" data-id="element-10848">
                <div className="absolute inset-0 flex items-center" data-id="element-10849">
                  <div className="w-full border-t border-gray-200" data-id="element-10850"></div>
                </div>
                <div className="relative flex justify-center" data-id="element-10851">
                  <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-10852">
                    Status
                  </span>
                </div>
              </div>

              {/* Status */}
              <div className="space-y-5" data-id="element-10853">
                <div data-id="element-10854">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-10855">
                    Status
                  </label>
                  <select value={productForm.isActive ? 'active' : 'inactive'} onChange={e => setProductForm({
                  ...productForm,
                  isActive: e.target.value === 'active'
                })} className="w-full md:w-1/2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors bg-white" data-id="element-10856">
                    <option value="active" data-id="element-10857">Active</option>
                    <option value="inactive" data-id="element-10858">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-8 py-4 border-t border-gray-100 bg-gray-50" data-id="element-10859">
              <button type="button" onClick={() => setShowProductForm(false)} className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-10860">
                Cancel
              </button>
              <button type="submit" className="px-6 py-2.5 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-10861">
                {editingProduct ? 'Save changes' : 'Create product'}
              </button>
            </div>
          </div>
        </form>
      </div>;
  }
  // Render Detail Mode
  if (!shop) return null;
  return <div className="max-w-[1400px] mx-auto space-y-8 pb-12" data-id="element-10862">
      <div className="flex items-center" data-id="element-10863">
        <button onClick={() => navigate('/shops')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold" data-id="element-10864">
          <ArrowLeftIcon size={20} className="mr-2" data-id="element-10865" />
          All shops
        </button>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-10866">
        <div className="flex items-center gap-4" data-id="element-10867">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{
          backgroundColor: shop.color
        }} data-id="element-10868">
            <span className="text-2xl font-bold text-red-600" data-id="element-10869">{shop.logo}</span>
          </div>
          <div data-id="element-10870">
            <div className="flex items-center gap-3" data-id="element-10871">
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-10872">
                {shop.name}
              </h1>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${shop.isOpen ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-10873">
                {shop.isOpen ? 'Open' : 'Closed'}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1.5" data-id="element-10874">
              {shop.channels.includes('pos') && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-10875">
                  POS
                </span>}
              {shop.channels.includes('ecommerce') && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-10876">
                  E-Commerce
                </span>}
              <span className="text-sm text-gray-500 ml-1" data-id="element-10877">
                {shop.description}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-10878">
          <button onClick={() => navigate(`/shop-details?id=${shop.id}&edit=true`)} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-transparent hover:border-gray-200" title="Edit Shop" data-id="element-10879">
            <EditIcon size={18} data-id="element-10880" />
          </button>
          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-transparent hover:border-gray-200" title="QR Code" data-id="element-10881">
            <QrCodeIcon size={18} data-id="element-10882" />
          </button>
          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-transparent hover:border-gray-200" title="Shop Link" data-id="element-10883">
            <LinkIcon size={18} data-id="element-10884" />
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-10885">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-10886">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-10887">
            <PackageIcon size={16} className="text-gray-500" data-id="element-10888" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-10889">
              Products
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-10890">
            <div className="text-2xl font-bold text-gray-900" data-id="element-10891">
              {shop.productsCount}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-10892">
            Active items in catalog
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-10893">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-10894">
            <DollarSignIcon size={16} className="text-gray-500" data-id="element-10895" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-10896">Revenue</span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-10897">
            <div className="text-2xl font-bold text-gray-900" data-id="element-10898">
              THB {shop.revenue.total.toLocaleString()}
            </div>
          </div>
          <div className={`px-4 py-2 text-xs font-medium ${shop.revenue.growth >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-id="element-10899">
            {shop.revenue.growth >= 0 ? '+' : ''}
            {shop.revenue.growth}% from last month
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-10900">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-10901">
            <ShoppingBagIcon size={16} className="text-gray-500" data-id="element-10902" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-10903">Orders</span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-10904">
            <div className="text-2xl font-bold text-gray-900" data-id="element-10905">
              {shop.ordersCount}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-10906">
            Total completed orders
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-10907">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-10908">
            <StoreIcon size={16} className="text-gray-500" data-id="element-10909" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-10910">Status</span>
          </div>
          <div className="px-4 py-4 flex-1 flex items-center" data-id="element-10911">
            {shop.isOpen ? <div className="flex items-center text-green-600" data-id="element-10912">
                <CheckCircleIcon size={24} className="mr-2" data-id="element-10913" />
                <span className="text-xl font-bold" data-id="element-10914">Accepting Orders</span>
              </div> : <div className="flex items-center text-red-600" data-id="element-10915">
                <XCircleIcon size={24} className="mr-2" data-id="element-10916" />
                <span className="text-xl font-bold" data-id="element-10917">Currently Closed</span>
              </div>}
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-10918">
            Toggle in edit mode
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-10919">
        <div className="flex space-x-8" data-id="element-10920">
          {[{
          id: 'dashboard',
          label: 'Dashboard'
        }, {
          id: 'products',
          label: 'Products'
        }, {
          id: 'categories',
          label: 'Categories'
        }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`pb-4 -mb-px text-sm font-medium transition-colors border-b-2 ${activeTab === tab.id ? 'border-[#F7941D] text-[#F7941D]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-10921">
              {tab.label}
            </button>)}
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-6" data-id="element-10922">
        {activeTab === 'dashboard' && <ShopDashboard shop={shop} data-id="element-10923" />}

        {activeTab === 'products' && <div className="space-y-6" data-id="element-10924">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-10925">
              <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-10926">
                <div className="relative flex-1 sm:w-64" data-id="element-10927">
                  <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-10928" />
                  <input type="text" placeholder="Search products..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-10929" />
                </div>
                <div className="relative" data-id="element-10930">
                  <select value={selectedCategoryId} onChange={e => setSelectedCategoryId(e.target.value)} className="appearance-none pl-4 pr-10 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors cursor-pointer text-gray-700" data-id="element-10931">
                    <option value="all" data-id="element-10932">All Categories</option>
                    {categories.map(c => <option key={c.id} value={c.id} data-id="element-10933">
                        {c.name}
                      </option>)}
                  </select>
                  <FilterIcon size={14} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-10934" />
                </div>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-10935">
                <div className="flex bg-gray-100 p-1 rounded-lg" data-id="element-10936">
                  <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-10937">
                    <LayoutGridIcon size={16} data-id="element-10938" />
                  </button>
                  <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-10939">
                    <ListIcon size={16} data-id="element-10940" />
                  </button>
                </div>
                <button onClick={handleAddProductClick} className="flex-1 sm:flex-none px-4 py-2 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center justify-center" data-id="element-10941">
                  <PlusIcon size={16} className="mr-2" data-id="element-10942" />
                  Add Product
                </button>
              </div>
            </div>

            {viewMode === 'grid' ? <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-id="element-10943">
                {filteredProducts.map(product => <div key={product.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm group" data-id="element-10944">
                    <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden bg-gray-100 relative" data-id="element-10945">
                      <img src={product.imageUrl} alt={product.name} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300" data-id="element-10946" />
                      {!product.isActive && <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center" data-id="element-10947">
                          <span className="px-3 py-1 bg-gray-900 text-white text-xs font-medium rounded-full shadow-sm" data-id="element-10948">
                            Inactive
                          </span>
                        </div>}
                    </div>
                    <div className="p-4" data-id="element-10949">
                      <h3 className="text-sm font-semibold text-gray-900 truncate" data-id="element-10950">
                        {product.name}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2 h-8" data-id="element-10951">
                        {product.description}
                      </p>
                      <div className="mt-3 flex justify-between items-end" data-id="element-10952">
                        <div data-id="element-10953">
                          {product.discountedPrice ? <div className="flex flex-col" data-id="element-10954">
                              <span className="text-xs text-gray-400 line-through" data-id="element-10955">
                                THB {product.price.toFixed(2)}
                              </span>
                              <span className="text-sm font-bold text-gray-900" data-id="element-10956">
                                THB {product.discountedPrice.toFixed(2)}
                              </span>
                            </div> : <span className="text-sm font-bold text-gray-900" data-id="element-10957">
                              THB {product.price.toFixed(2)}
                            </span>}
                        </div>
                        <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-md" data-id="element-10958">
                          Stock: {product.stockQuantity}
                        </span>
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center" data-id="element-10959">
                        <span className="text-xs text-gray-400 font-mono" data-id="element-10960">
                          {product.sku}
                        </span>
                        <div className="flex gap-1" data-id="element-10961">
                          <button onClick={() => toggleProductStatus(product.id)} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-10962">
                            <EyeIcon size={14} data-id="element-10963" />
                          </button>
                          <button onClick={() => handleEditProductClick(product)} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-10964">
                            <EditIcon size={14} data-id="element-10965" />
                          </button>
                          <button onClick={() => handleDeleteProductClick(product.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" data-id="element-10966">
                            <TrashIcon size={14} data-id="element-10967" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div> : <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-10968">
                <div className="overflow-x-auto" data-id="element-10969">
                  <table className="w-full text-left border-collapse" data-id="element-10970">
                    <thead data-id="element-10971">
                      <tr className="border-b border-gray-100 bg-gray-50/50" data-id="element-10972">
                        <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10973">
                          Product
                        </th>
                        <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10974">
                          Category
                        </th>
                        <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10975">
                          Price
                        </th>
                        <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10976">
                          Stock
                        </th>
                        <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10977">
                          SKU
                        </th>
                        <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10978">
                          Status
                        </th>
                        <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-10979">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100" data-id="element-10980">
                      {filteredProducts.map(product => {
                  const category = categories.find(c => c.id === product.categoryId);
                  return <tr key={product.id} className="hover:bg-gray-50 transition-colors" data-id="element-10981">
                            <td className="px-6 py-4" data-id="element-10982">
                              <div className="flex items-center gap-3" data-id="element-10983">
                                <div className="h-10 w-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-200" data-id="element-10984">
                                  <img src={product.imageUrl} alt={product.name} className="h-full w-full object-cover" data-id="element-10985" />
                                </div>
                                <div data-id="element-10986">
                                  <div className="text-sm font-medium text-gray-900" data-id="element-10987">
                                    {product.name}
                                  </div>
                                  <div className="text-xs text-gray-500 truncate max-w-[200px] mt-0.5" data-id="element-10988">
                                    {product.description}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4" data-id="element-10989">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200" data-id="element-10990">
                                {category?.name || 'Uncategorized'}
                              </span>
                            </td>
                            <td className="px-6 py-4" data-id="element-10991">
                              {product.discountedPrice ? <div data-id="element-10992">
                                  <div className="text-sm font-medium text-gray-900" data-id="element-10993">
                                    THB {product.discountedPrice.toFixed(2)}
                                  </div>
                                  <div className="text-xs text-gray-400 line-through mt-0.5" data-id="element-10994">
                                    THB {product.price.toFixed(2)}
                                  </div>
                                </div> : <div className="text-sm font-medium text-gray-900" data-id="element-10995">
                                  THB {product.price.toFixed(2)}
                                </div>}
                            </td>
                            <td className="px-6 py-4" data-id="element-10996">
                              <span className={`text-sm font-medium ${product.stockQuantity < 10 ? 'text-red-600' : 'text-gray-900'}`} data-id="element-10997">
                                {product.stockQuantity}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 font-mono text-xs" data-id="element-10998">
                              {product.sku}
                            </td>
                            <td className="px-6 py-4" data-id="element-10999">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${product.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`} data-id="element-11000">
                                {product.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right" data-id="element-11001">
                              <div className="flex justify-end gap-1" data-id="element-11002">
                                <button onClick={() => toggleProductStatus(product.id)} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-11003">
                                  <EyeIcon size={16} data-id="element-11004" />
                                </button>
                                <button onClick={() => handleEditProductClick(product)} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-11005">
                                  <EditIcon size={16} data-id="element-11006" />
                                </button>
                                <button onClick={() => handleDeleteProductClick(product.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" data-id="element-11007">
                                  <TrashIcon size={16} data-id="element-11008" />
                                </button>
                              </div>
                            </td>
                          </tr>;
                })}
                    </tbody>
                  </table>
                </div>
              </div>}
          </div>}

        {activeTab === 'categories' && <div className="space-y-6" data-id="element-11009">
            <div className="flex justify-between items-center" data-id="element-11010">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-11011">
                Product Categories
              </h3>
              <button className="px-4 py-2 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center" data-id="element-11012">
                <PlusIcon size={16} className="mr-2" data-id="element-11013" />
                Add Category
              </button>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-11014">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-11015">
                <thead className="bg-gray-50/50" data-id="element-11016">
                  <tr data-id="element-11017">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11018">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11019">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11020">
                      Products
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11021">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100" data-id="element-11022">
                  {categories.map(category => <tr key={category.id} className="hover:bg-gray-50 transition-colors" data-id="element-11023">
                      <td className="px-6 py-4" data-id="element-11024">
                        <div className="flex items-center gap-3" data-id="element-11025">
                          <div className="h-8 w-8 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200" data-id="element-11026">
                            <TagIcon size={14} className="text-gray-500" data-id="element-11027" />
                          </div>
                          <div className="text-sm font-medium text-gray-900" data-id="element-11028">
                            {category.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-11029">
                        <div className="text-sm text-gray-500" data-id="element-11030">
                          {category.description}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-11031">
                        <div className="text-sm font-medium text-gray-900" data-id="element-11032">
                          {category.productCount}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-11033">
                        <div className="flex justify-end gap-1" data-id="element-11034">
                          <button className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-11035">
                            <EditIcon size={16} data-id="element-11036" />
                          </button>
                          <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" data-id="element-11037">
                            <TrashIcon size={16} data-id="element-11038" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}
      </div>
    </div>;
}